/**
 * Preview-environment context, read from the Vite build-time env.
 *
 * Populated only inside a cis-preview container (docker/preview-entrypoint.sh
 * writes the VITE_PREVIEW_* vars into apps/frontend/.env). In the live app none
 * of these are set, so `readPreviewContext()` returns null and the banner
 * renders nothing.
 *
 * This is shell code, so touching `import.meta.env` here is allowed. Kept
 * separate from the banner component so it can be unit-tested and so stories can
 * inject a context directly rather than mutating build-time env.
 */

export type PreviewDataMode = "mock" | "full_stack";

export interface PreviewContext {
  /** Branch the preview was built from (e.g. "feature/my-thing"). */
  branch: string;
  /** Which data the preview runs on: fixtures ("mock") or a copy of live data. */
  dataMode: PreviewDataMode;
  /** ISO-8601 hard-TTL deadline, or null when unset/unparseable. */
  expiresAt: Date | null;
  /** Sharable URL, or null when the control plane could not build one. */
  shareUrl: string | null;
  /** A testing preview (quote-to-invoice-e2e-testing 06 §1): the banner also shows the preview's clock. */
  testing: boolean;
  /** A testing preview's environment id (06 §4.1), added to feedback context; null elsewhere. */
  environmentId: string | null;
}

function env(): Record<string, string | undefined> {
  return (
    (import.meta as { env?: Record<string, string | undefined> }).env ?? {}
  );
}

function parseExpiry(raw: string | undefined): Date | null {
  if (!raw) return null;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Return the preview context, or null when not running inside a preview
 * environment (the only signal is `VITE_PREVIEW === "1"`).
 */
export function readPreviewContext(): PreviewContext | null {
  const e = env();
  if (e.VITE_PREVIEW !== "1") return null;

  const dataMode: PreviewDataMode =
    e.VITE_PREVIEW_DATA_MODE === "full_stack" ? "full_stack" : "mock";

  const shareUrl = e.VITE_PREVIEW_SHARE_URL?.trim();

  return {
    branch: e.VITE_PREVIEW_BRANCH?.trim() || "unknown",
    dataMode,
    expiresAt: parseExpiry(e.VITE_PREVIEW_EXPIRES_AT),
    shareUrl: shareUrl ? shareUrl : null,
    testing: e.VITE_PREVIEW_TESTING === "1",
    environmentId: e.VITE_PREVIEW_ENVIRONMENT_ID?.trim() || null,
  };
}
